import { Dpr, RootState } from '@react-three/fiber';
import { Camera, MOUSE, TOUCH } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { Point2D } from '@paragon/three-shared';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { CameraType } from '../store/slices/camera/types';
import { DesignClientCamera } from './DesignClientCamera';
import { OrthographicLayoutCamera } from './OrthographicLayoutCamera';
import { FAR_FRUSTUM, NEAR_FRUSTUM, PerspectiveLayoutCamera } from './PerspectiveLayoutCamera';
import { setReactThreeFiberEventsEnabled } from './reactThreeFiberEventHandling';
import {
  clearSceneHoverFor2DCameraDragIfApplicable,
  createOrthographicDesignCamera,
  focusCameraOnSelectedObjectOrFallback,
  registerViewportDomElement,
  switchToActiveCameraType,
  syncCameraPropertiesToRedux,
} from './ThreeJsSceneActions';

let size: { width: number; height: number } | undefined;
let set: RootState['set'] | undefined;
let get: RootState['get'] | undefined;
let setDpr: ((dpr: Dpr) => void) | undefined;
let invalidate: () => void;

let getCanvasElement: () => HTMLCanvasElement | null = () => null;
let getViewportDiv: () => HTMLDivElement | null = () => null;
let hasViewportDomElementBeenRegistered = false;

export function registerReactThreeFiber(
  state: RootState,
  canvasElementProvider: () => HTMLCanvasElement | null,
  viewportProvider: () => HTMLDivElement | null,
) {
  size = state.size;
  set = state.set;
  get = state.get;
  setDpr = state.setDpr;
  invalidate = state.invalidate;

  getViewportDiv = viewportProvider;
  getCanvasElement = canvasElementProvider;

  if (!hasViewportDomElementBeenRegistered) {
    const viewportDiv = getViewportDiv();
    if (!viewportDiv) {
      throw new Error('Viewport div was null.');
    }

    switchToActiveCameraType();
    focusCameraOnSelectedObjectOrFallback();

    registerViewportDomElement(viewportDiv);
    hasViewportDomElementBeenRegistered = true;
  }
}

export function getSize(): { width: number; height: number } {
  if (!size) {
    throw new Error('The size should have been set by now.');
  }
  return size;
}

export function getLayoutModeCameraControls(): OrbitControls {
  if (!get) throw new Error('get should have been set by now.');

  const result = get().controls;
  if (!result) throw new Error('Camera controls should have been set by now.');

  return result as OrbitControls;
}

export function getLayoutModeCameraControlsOrNull(): OrbitControls | null {
  if (!get) return null;

  const result = get().controls;
  if (!result) return null;

  return result as OrbitControls;
}

export function setDevicePixelRatio(devicePixelRatio: number) {
  if (!setDpr) {
    throw new Error('setDpr should have been set by now.');
  }
  return setDpr(devicePixelRatio);
}

export function invalidateFrameIfInitialized() {
  if (invalidate) invalidate();
}

export function invalidateFrame() {
  if (!invalidate) {
    throw new Error('invalidate should have been set by now.');
  }

  invalidate();
}

function setCameraAndControls(
  callback: (args: { viewportDiv: HTMLDivElement; canvasElement: HTMLCanvasElement }) => {
    camera: RootState['camera'];
    controls: OrbitControls | null;
  },
) {
  if (!get) throw new Error('get should have been set by now.');
  if (!set) throw new Error('set should have been set by now.');

  const viewportDiv = getViewportDiv();
  if (!viewportDiv) throw new Error('Viewport div was null.');

  const canvasElement = getCanvasElement();
  if (!canvasElement) throw new Error('Canvas element was null');

  (get().controls as OrbitControls | null)?.dispose();
  set(callback({ viewportDiv, canvasElement }));
  setReactThreeFiberEventsEnabled(get(), true);
}

export function switchToCameraType(type: CameraType) {
  switch (type) {
    case 'orthographic-design':
      switchToOrthographicDesignCamera();
      break;

    case 'orthographic-layout':
      switchToOrthographicLayoutCamera();
      break;

    case 'perspective-layout':
      switchToPerspectiveLayoutCamera();
      break;

    default:
      assertExhaustiveSwitch(type);
  }
}

function switchToOrthographicDesignCamera() {
  setCameraAndControls(({ viewportDiv }) => ({
    camera: createOrthographicDesignCamera(viewportDiv.clientWidth / viewportDiv.clientHeight),
    controls: null,
  }));
}

function switchToOrthographicLayoutCamera() {
  setCameraAndControls(({ viewportDiv, canvasElement }) => {
    const camera = new OrthographicLayoutCamera(viewportDiv.clientWidth, viewportDiv.clientHeight);
    const controls = createStandardControls(camera, canvasElement);

    controls.enableRotate = false;

    return { camera, controls };
  });
}

function switchToPerspectiveLayoutCamera() {
  setCameraAndControls(({ viewportDiv, canvasElement }) => {
    const aspect = viewportDiv.clientWidth / viewportDiv.clientHeight;
    const camera = new PerspectiveLayoutCamera(aspect);
    const controls = createStandardControls(camera, canvasElement);

    return { camera, controls };
  });
}

function createStandardControls(camera: Camera, canvasElement: HTMLCanvasElement) {
  const controls = new OrbitControls(camera, canvasElement);

  controls.screenSpacePanning = false;
  controls.zoomToCursor = true;

  controls.minDistance = NEAR_FRUSTUM;
  controls.maxDistance = FAR_FRUSTUM;

  controls.mouseButtons = {
    LEFT: MOUSE.PAN,
    MIDDLE: MOUSE.PAN,
    RIGHT: MOUSE.ROTATE,
  };

  controls.touches = {
    ONE: TOUCH.PAN,
    TWO: TOUCH.DOLLY_ROTATE,
  };

  // I know this looks silly, but trust me, don't remove these flags or alter these event handlers unless you have a lot
  //   of free time and don't mind spending it debugging cross-platform differences in context menu behavior. -- David
  let interactionStarted = false;
  let cameraChangedDuringCurrentInteraction = false;

  controls.addEventListener('start', () => {
    interactionStarted = true;
    cameraChangedDuringCurrentInteraction = false;
  });

  controls.addEventListener('change', () => {
    invalidate();

    if (interactionStarted) {
      if (!cameraChangedDuringCurrentInteraction && clearSceneHoverFor2DCameraDragIfApplicable()) {
        setReactThreeFiberEventsEnabled(get!(), false);
      }
      cameraChangedDuringCurrentInteraction = true;
    } else {
      // Expected; the change event fires once when we switch to a camera. Since an interaction is not actually
      //   happening (and the camera switch is synchronized by other means), this needs to be ignored.
      //
      // Don't alter this behavior without testing context menus on macOS.
    }
  });

  controls.addEventListener('end', () => {
    if (!interactionStarted) return;

    setReactThreeFiberEventsEnabled(get!(), true);
    if (cameraChangedDuringCurrentInteraction) syncCameraPropertiesToRedux();

    interactionStarted = false;
    cameraChangedDuringCurrentInteraction = false;
  });

  return controls;
}

/**
 * A PNG data URL of the current viewport, for attaching to a problem report. Returns `null` if the scene isn't up yet
 * or the capture fails — a report without a picture is still worth sending.
 *
 * The render and the read have to stay in the same synchronous block. WebGL clears the drawing buffer once the browser
 * composites, so a `toDataURL` that happens any later reads an empty buffer and produces a blank image. The usual
 * workaround is `preserveDrawingBuffer: true` on the renderer, but that costs every user memory and speed on every
 * frame forever for the sake of a rarely-used feature. Drawing immediately before reading avoids it entirely.
 *
 * This is safe here because `SceneWrapper` uses `frameloop="demand"`: there's no continuous render loop to race
 * against. If that ever changes to a continuous loop, revisit this.
 */
export function captureViewportPngOrNull(): string | null {
  if (!get) return null;

  const canvasElement = getCanvasElement();
  if (!canvasElement) return null;

  try {
    const { gl, scene, camera } = get();
    gl.render(scene, camera);
    return canvasElement.toDataURL('image/png');
  } catch {
    return null;
  }
}

export function getActiveCameraOrNull() {
  if (!get) return null;
  return get().camera as unknown as DesignClientCamera; // TODO: typescript
}

export function getActiveCamera() {
  const activeCamera = getActiveCameraOrNull();
  if (!activeCamera) throw new Error('The camera has not been created yet.');
  return activeCamera;
}

export function pixelSpaceToViewportSpace(xPixelCoord: number, yPixelCoord: number): Point2D {
  const size = getSize();
  return { x: (xPixelCoord / size.width) * 2 - 1, y: -(yPixelCoord / size.height) * 2 + 1 };
}

export function pixelDeltaToViewportDelta(xPixelDelta: number, yPixelDelta: number): Point2D {
  const viewportCoords = pixelSpaceToViewportSpace(xPixelDelta, yPixelDelta);
  return { x: viewportCoords.x + 1, y: viewportCoords.y - 1 };
}
